import { Badge, Button, Card, cn, Skeleton } from '@menage/ui';
import type { UseQueryResult } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, type LucideIcon } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useErrorMessage } from '@/lib/query';

export function PageHeader({ title, description, actions, back, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; back?: string; className?: string }) {
  const { t } = useTranslation();
  return (
    <div className={cn('mb-5 flex flex-col gap-3 sm:mb-6 sm:flex-row sm:items-end sm:justify-between', className)}>
      <div className="min-w-0 space-y-1">
        {back ? (
          <Link to={back} className="mb-1 inline-flex min-h-9 items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> {t('actions.back')}
          </Link>
        ) : null}
        <h1 className="text-2xl font-semibold leading-tight sm:text-[1.7rem]">{title}</h1>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      {actions ? <div className="flex flex-wrap gap-2">{actions}</div> : null}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action, className }: { icon?: LucideIcon; title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center rounded-xl border border-dashed border-border bg-card/60 px-6 py-10 text-center', className)}>
      {Icon ? (
        <div className="mb-3 rounded-full bg-secondary p-3 text-secondary-foreground">
          <Icon className="size-6" />
        </div>
      ) : null}
      <p className="font-medium">{title}</p>
      {description ? <p className="mt-1 max-w-sm text-sm text-muted-foreground">{description}</p> : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = useErrorMessage();
  const { t } = useTranslation();
  return (
    <div role="alert" className="flex flex-col items-center rounded-xl border border-border bg-card px-6 py-8 text-center">
      <AlertTriangle className="mb-2 size-6 text-tone-amber" />
      <p className="text-sm">{message(error, 'load')}</p>
      {onRetry ? (
        <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
          {t('actions.retry')}
        </Button>
      ) : null}
    </div>
  );
}

export function ListSkeleton({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('space-y-3', className)} aria-busy="true" aria-live="polite">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-20 w-full" />
      ))}
    </div>
  );
}

/** Renders loading / error / data states of a query uniformly. */
export function QueryView<T>({ query, skeleton, children }: { query: UseQueryResult<T>; skeleton?: ReactNode; children: (data: T) => ReactNode }) {
  if (query.isPending) return <>{skeleton ?? <ListSkeleton />}</>;
  if (query.isError) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  return <>{children(query.data)}</>;
}

export function StatCard({ label, value, hint, icon: Icon, tone = 'default', to }: { label: ReactNode; value: ReactNode; hint?: ReactNode; icon?: LucideIcon; tone?: 'default' | 'warning' | 'success'; to?: string }) {
  const body = (
    <Card className={cn('h-full p-4 transition-colors', to && 'hover:border-primary/40')}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">{label}</p>
        {Icon ? <Icon className={cn('size-4 text-muted-foreground', tone === 'warning' && 'text-tone-amber', tone === 'success' && 'text-tone-green')} /> : null}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums">{value}</p>
      {hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </Card>
  );
  return to ? (
    <Link to={to} className="block rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
      {body}
    </Link>
  ) : (
    body
  );
}

export function Pager({ page, pageSize, total, onPage }: { page: number; pageSize: number; total: number; onPage: (p: number) => void }) {
  const { t } = useTranslation();
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (pages <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-between gap-2 text-sm">
      <span className="text-muted-foreground">{t('labels.page', { page, pages })}</span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          {t('actions.previous')}
        </Button>
        <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          {t('actions.next')}
        </Button>
      </div>
    </div>
  );
}

export function InfoRow({ icon: Icon, label, children }: { icon?: LucideIcon; label?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 py-1.5 text-sm">
      {Icon ? <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" /> : null}
      <div className="min-w-0">
        {label ? <p className="text-xs text-muted-foreground">{label}</p> : null}
        <div className="break-words">{children}</div>
      </div>
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 mt-6 flex items-center justify-between gap-2 first:mt-0">
      <h2 className="text-base font-semibold">{children}</h2>
      {action}
    </div>
  );
}

export function Pill({ children, className }: { children: ReactNode; className?: string }) {
  return <Badge tone="outline" className={className}>{children}</Badge>;
}

/** Last line of defence: a crash in one screen never shows a technical error. */
export class ErrorBoundary extends Component<{ children: ReactNode; fallback?: ReactNode }, { failed: boolean }> {
  override state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('UI error', error, info.componentStack);
  }
  override render() {
    if (this.state.failed) {
      return (
        this.props.fallback ?? (
          <div className="mx-auto max-w-md p-8 text-center">
            <AlertTriangle className="mx-auto mb-3 size-8 text-tone-amber" />
            <p className="font-medium">Une erreur est survenue. / Something went wrong.</p>
            <Button className="mt-4" onClick={() => window.location.reload()}>
              Recharger / Reload
            </Button>
          </div>
        )
      );
    }
    return this.props.children;
  }
}
